import React, { useState, useEffect } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../ui/dialog';
import { Alert, AlertDescription } from '../../ui/alert';
import { toast } from 'sonner';
import { Key, Bot, Eye, EyeOff, CheckCircle2, AlertCircle, ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export const APIKeySettingsDialog = ({ open, onClose, onSave }) => {
  const [openaiKey, setOpenaiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [enableAI, setEnableAI] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    if (open) {
      loadSettings();
    }
  }, [open]);

  const loadSettings = () => {
    // Load from localStorage
    const savedKey = localStorage.getItem('openai_api_key') || '';
    const aiEnabled = localStorage.getItem('ai_assistant_enabled') === 'true';
    setOpenaiKey(savedKey);
    setEnableAI(aiEnabled);
    setTestResult(null);
  };

  const testAPIKey = async () => {
    if (!openaiKey.trim()) {
      toast.error('Please enter an API key');
      return;
    }

    setTesting(true);
    setTestResult(null);

    try {
      const response = await fetch('https://api.openai.com/v1/models', {
        headers: {
          'Authorization': `Bearer ${openaiKey}`,
        },
      });

      if (response.ok) {
        setTestResult({ success: true, message: 'API key is valid!' });
        toast.success('API key verified successfully');
      } else {
        const error = await response.json();
        setTestResult({ 
          success: false, 
          message: error.error?.message || 'Invalid API key' 
        });
        toast.error('Invalid API key');
      }
    } catch (error) {
      setTestResult({ 
        success: false, 
        message: 'Failed to test API key. Check your internet connection.' 
      });
      toast.error('Connection failed');
    } finally {
      setTesting(false);
    }
  };

  const handleSave = () => {
    // Save to localStorage
    if (openaiKey.trim()) {
      localStorage.setItem('openai_api_key', openaiKey.trim());
    } else {
      localStorage.removeItem('openai_api_key');
    }
    localStorage.setItem('ai_assistant_enabled', enableAI.toString());
    
    toast.success('Settings saved');
    onSave && onSave({ openaiKey: openaiKey.trim(), enableAI });
    onClose();
  };

  const maskKey = (key) => {
    if (!key || key.length < 10) return key;
    return key.slice(0, 7) + '...' + key.slice(-4);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Key className="h-5 w-5" />
            AI Assistant Settings
          </DialogTitle>
          <DialogDescription>
            Add your OpenAI API key to enable AI-powered features like voice transcription and smart suggestions.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Enable AI Toggle */}
          <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
            <div className="flex items-center gap-3">
              <Bot className="h-5 w-5 text-violet-600" />
              <div>
                <p className="font-medium">Enable AI Assistant</p>
                <p className="text-sm text-muted-foreground">
                  Voice transcription & smart suggestions
                </p>
              </div>
            </div>
            <Switch
              checked={enableAI}
              onCheckedChange={setEnableAI}
            />
          </div>

          {enableAI && (
            <>
              {/* API Key Input */}
              <div className="space-y-2">
                <Label htmlFor="openai-key">OpenAI API Key</Label>
                <div className="relative">
                  <Input
                    id="openai-key"
                    type={showKey ? 'text' : 'password'}
                    value={openaiKey}
                    onChange={(e) => setOpenaiKey(e.target.value)}
                    placeholder="sk-..."
                    className="pr-20"
                  />
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => setShowKey(!showKey)}
                    >
                      {showKey ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Your key is stored locally and never sent to our servers.
                </p>
              </div>

              {/* Test Button */}
              <Button
                variant="outline"
                className="w-full"
                onClick={testAPIKey}
                disabled={testing || !openaiKey.trim()}
              >
                {testing ? 'Testing...' : 'Test API Key'}
              </Button>

              {/* Test Result */}
              {testResult && (
                <Alert variant={testResult.success ? 'default' : 'destructive'}>
                  {testResult.success ? (
                    <CheckCircle2 className="h-4 w-4" />
                  ) : (
                    <AlertCircle className="h-4 w-4" />
                  )}
                  <AlertDescription>{testResult.message}</AlertDescription>
                </Alert>
              )}

              {/* Help Link */}
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Don't have an API key?</strong>
                </p>
                <a
                  href="https://platform.openai.com/api-keys"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline flex items-center gap-1 mt-1"
                >
                  Get your key from OpenAI
                  <ExternalLink className="h-3 w-3" />
                </a>
              </div>

              {/* Features List */}
              <div className="space-y-2">
                <p className="text-sm font-medium">What you get:</p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-3 w-3 text-green-600" />
                    Voice-to-text call disposition
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-3 w-3 text-green-600" />
                    Smart stage change suggestions
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-3 w-3 text-green-600" />
                    Automated call summaries
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-3 w-3 text-green-600" />
                    Next action recommendations
                  </li>
                </ul>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            Save Settings
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default APIKeySettingsDialog;
